import React from 'react';
import { Title } from '@/components/ui/title';
import { Box, Circle, HStack, Icon, List, Text, VStack } from '@chakra-ui/react';
import { LuCheck, LuFileCheck, LuLock, LuMail, LuShieldCheck } from 'react-icons/lu';
import CopyableField from '@/components/common/CopyableField.tsx';

export const UPLOAD_GUIDANCE_TITLE = 'What should I upload?';

const IDEAL_ITEMS = [
	'House plans',
	'Consent documents',
	'Specifications',
	'Code Compliance documents',
	'Builder Guarantees',
	'Product maintenance or warranties',
	'User manuals',
	'Project contacts for builders, trades, & professionals',
	'Proof of maintenance & work done',
];

interface UploadGuidanceContentProps {
	uploadEmail?: string;
	senderEmail?: string;
	showEmailUpload?: boolean;
}

const UploadGuidanceContent: React.FC<UploadGuidanceContentProps> = (props) => {
	const { uploadEmail, senderEmail, showEmailUpload = true } = props;

	return (
		<VStack align="start" gap={6} w="full">
			<Text textStyle="label.lg" lineHeight="24px">
				The more information you add, the more useful Bundle becomes.
			</Text>

			<VStack align="start" gap={2} w="full">
				<HStack gap={2} align="center">
					<Circle size="20px" bg="green.600" flexShrink={0}>
						<Icon as={LuCheck} boxSize="12px" color="white" strokeWidth={3} />
					</Circle>
					<Title level="subsection" lineHeight="28px">
						Ideal documents:
					</Title>
				</HStack>

				<List.Root
					gap={4}
					pl="48px"
					w="full"
					css={{ '& li::marker': { color: 'var(--chakra-colors-text-default)' } }}
				>
					{IDEAL_ITEMS.map((item) => (
						<List.Item key={item} fontSize="lg" fontWeight="normal" lineHeight="28px">
							{item}
						</List.Item>
					))}
				</List.Root>
			</VStack>

			<Box pl="12px">
				<Text fontSize="md" fontStyle="italic" lineHeight="24px">
					Start with whatever you have handy - you will always have the opportunity to add more.
				</Text>
			</Box>

			<Box w="full" h="1px" bg="border.default" />

			<VStack align="start" gap={0} w="full">
				<Text fontSize="lg" fontWeight="semibold" lineHeight="28px">
					GOOD TO KNOW
				</Text>

				<HStack align="start" gap={3} py={2} pr={2}>
					<Box color="brand.fg" flexShrink={0} mt="2px">
						<LuShieldCheck size={20} />
					</Box>
					<Text fontSize="md" lineHeight="24px">
						You control who has access to your Bundle. We recommend not uploading sensitive records like
						personal ID, bank, or financial documents.
					</Text>
				</HStack>

				<HStack align="start" gap={3} py={2} pr={2}>
					<Box color="brand.fg" flexShrink={0} mt="2px">
						<LuLock size={20} />
					</Box>
					<Text fontSize="md" lineHeight="24px">
						Need to keep something sensitive? Mark any file private with the lock - private files stay
						visible only to you and admins, even when your Bundle is shared.
					</Text>
				</HStack>

				<HStack align="start" gap={3} py={2} w="full">
					<Box color="brand.fg" flexShrink={0} mt="2px">
						<LuFileCheck size={20} />
					</Box>
					<Text fontSize="md" lineHeight="24px">
						Supported files: Bundle currently understands .pdf, .xlsx, .docx, .txt, .md, & common image
						formats - Max accepted size is 50MB per file.
					</Text>
				</HStack>

				{showEmailUpload && (
					<HStack align="start" gap={3} py={2} w="full">
						<Box color="brand.fg" flexShrink={0} mt="2px">
							<LuMail size={20} />
						</Box>
						<VStack align="stretch" gap={2} flex={1} minW={0}>
							<Text fontSize="md" lineHeight="24px">
								You can also upload attachments by forwarding an email to the property at this address:
							</Text>
							{uploadEmail ? (
								<CopyableField value={uploadEmail} truncate />
							) : (
								<Text fontSize="sm" color="fg.muted" lineHeight="20px">
									The property's upload email address will appear here when available.
								</Text>
							)}
							{senderEmail && (
								<Text fontSize="md" lineHeight="24px">
									Must be sent from{' '}
									<Text as="span" fontWeight="semibold" color="brand.solid">
										{senderEmail}
									</Text>
									.
								</Text>
							)}
							<Text fontSize="md" lineHeight="24px">
								Bundle imports the attachments; the email body is ignored.
							</Text>
						</VStack>
					</HStack>
				)}
			</VStack>
		</VStack>
	);
};

export default UploadGuidanceContent;
